import CRMOrderDetails from '../../components/CRMOrderDetails';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Truck, Download, ExternalLink } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import ShippingLabelModal from '../../components/ShippingLabelModal';
import OrderItemDetails from '../../components/OrderItemDetails';
import StaffOrderNotes from '../../components/StaffOrderNotes';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  'pending', 'confirmed', 'in_production', 'ready_for_pickup',
  'shipped', 'delivered', 'completed', 'cancelled'
];

const statusColors = {
  pending: 'bg-yellow-500/10 text-yellow-500',
  confirmed: 'bg-blue-500/10 text-blue-500',
  in_production: 'bg-purple-500/10 text-purple-500',
  ready_for_pickup: 'bg-cyan-500/10 text-cyan-500',
  shipped: 'bg-indigo-500/10 text-indigo-500',
  delivered: 'bg-green-500/10 text-green-500',
  completed: 'bg-green-500/10 text-green-500',
  cancelled: 'bg-red-500/10 text-red-500'
};

const paymentColors = {
  'Paid in Full': 'bg-green-500/10 text-green-600',
  'Deposit Paid': 'bg-blue-500/10 text-blue-500',
  'Unpaid': 'bg-red-500/10 text-red-500'
};

const AddressBlock = ({ title, address }) => {
  if (!address || Object.keys(address).length === 0) return null;
  return (
    <div>
      <p className="text-sm font-medium text-muted-foreground mb-1">{title}</p>
      <div className="text-sm">
        {address.name && <p>{address.name}</p>}
        {(address.line1 || address.street) && <p>{address.line1 || address.street}</p>}
        {address.line2 && <p>{address.line2}</p>}
        <p>
          {[address.city, address.state, address.zip || address.postal_code]
            .filter(Boolean).join(', ')}
        </p>
      </div>
    </div>
  );
};

const AdminOrderDetail = () => {
  const { id } = useParams();
  const { token } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showShipping, setShowShipping] = useState(false);

  const fetchOrder = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/orders/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrder(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.status === 404
        ? 'Order not found.'
        : 'Failed to load order. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    if (token) fetchOrder();
  }, [token, fetchOrder]);

  const handleStatusChange = async (newStatus) => {
    if (!newStatus || newStatus === order.status) return;
    setUpdatingStatus(true);
    try {
      const response = await axios.put(
        `${API_URL}/api/orders/${id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOrder(response.data);
    } catch (err) {
      alert('Failed to update status: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUpdatingStatus(false);
    }
  };

  const money = (v) => `$${(v ?? 0).toFixed(2)}`;

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6 text-center text-muted-foreground">Loading order...</div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="p-6" data-testid="order-detail-error">
          <p className="text-red-500 mb-4">{error}</p>
          <Link to="/admin/orders" className="inline-flex items-center gap-2 text-primary hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to Orders
          </Link>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="admin-order-detail">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Link to="/admin/orders" className="p-2 hover:bg-accent rounded transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold break-words">{order.in_store_ticket || order.order_number}</h1>
              <p className="text-sm text-muted-foreground">
                Placed {new Date(order.created_at).toLocaleString()}
                {order.is_rush && <span className="ml-2 text-red-500 font-medium">RUSH</span>}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${paymentColors[order.payment_status] || 'bg-muted text-muted-foreground'}`}
              data-testid="payment-status-pill">
              {order.payment_status || 'Unknown'}
            </span>
            {order.pipeline_stage && <span className="text-sm font-medium">Prometheus: {order.pipeline_stage}</span>}
            <select
              value={order.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              disabled={updatingStatus}
              className={`px-3 py-1.5 text-sm font-medium rounded border border-border bg-input focus:outline-none focus:ring-2 focus:ring-primary ${statusColors[order.status] || ''}`}
              data-testid="order-status-select"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
              ))}
            </select>
            <button
              onClick={() => setShowShipping(true)}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded border border-border hover:bg-accent transition-colors"
              data-testid="create-label-button"
            >
              <Truck className="w-4 h-4" /> Shipping Label
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: items + uploads + shipments */}
          <div className="lg:col-span-2 min-w-0 space-y-6">
            <CRMOrderDetails details={order.crm_details} staff />
          {/* Items */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="p-4 border-b border-border font-medium">Items</div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/50 text-sm">
                    <th className="text-left p-3 font-medium">Product</th>
                    <th className="text-center p-3 font-medium">Qty</th>
                    <th className="text-right p-3 font-medium">Unit</th>
                    <th className="text-right p-3 font-medium">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {!order.items?.length && <tr><td colSpan={4} className="p-4 text-sm text-muted-foreground">Refer to the in-store ticket for item details.</td></tr>}
                  {order.items.map((item) => (
                    <tr key={item.id} className="border-b border-border last:border-0">
                      <td className="p-3">
                        <p className="font-medium">{item.product_name}</p>
                        {item.variant_name && (
                          <p className="text-sm text-muted-foreground">{item.variant_name}</p>
                        )}
                        <OrderItemDetails item={item} />
                      </td>
                      <td className="p-3 text-center">{item.quantity}</td>
                      <td className="p-3 text-right">{money(item.unit_price)}</td>
                      <td className="p-3 text-right font-medium">{money(item.total_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <StaffOrderNotes orderId={order.id} token={token} />
            {/* Artwork & files: per-item logos + order-level uploads, with previews */}
            {(order.uploads.length > 0 || order.items.some((it) => it.config?.logo_url)) && (
              <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="artwork-files-card">
                <div className="p-4 border-b border-border font-medium">Artwork &amp; Files</div>
                <div className="p-4 flex flex-wrap gap-3">
                  {order.items.filter((it) => it.config?.logo_url).map((it) => (
                    <a key={`logo-${it.id}`} href={it.config.logo_url} target="_blank" rel="noreferrer"
                      className="flex flex-col items-center gap-1.5 p-3 rounded-lg border border-border hover:border-primary/50 transition-colors w-32">
                      <img src={it.config.logo_url} alt="Logo"
                        className="w-20 h-20 rounded object-contain bg-white border border-border"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      <span className="text-xs font-medium text-center">Logo — {it.product_name.slice(0, 18)}</span>
                      <span className="inline-flex items-center gap-1 text-xs text-primary"><Download className="w-3 h-3" /> open</span>
                    </a>
                  ))}
                  {order.uploads.map((u) => (
                    <a key={u.id} href={u.file_url} target="_blank" rel="noreferrer"
                      className="flex flex-col items-center gap-1.5 p-3 rounded-lg border border-border hover:border-primary/50 transition-colors w-32">
                      {(u.file_type || '').startsWith('image/') ? (
                        <img src={u.file_url} alt="" className="w-20 h-20 rounded object-contain bg-white border border-border" />
                      ) : (
                        <span className="w-20 h-20 rounded bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground">
                          {(u.file_name || '').split('.').pop()?.toUpperCase() || 'FILE'}
                        </span>
                      )}
                      <span className="text-xs text-center truncate w-full" title={u.file_name}>{u.file_name}</span>
                      <span className="inline-flex items-center gap-1 text-xs text-primary"><Download className="w-3 h-3" /> download</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Shipments */}
            {order.shipments.length > 0 && (
              <div className="bg-card border border-border rounded-lg overflow-hidden">
                <div className="p-4 border-b border-border font-medium">Shipments</div>
                <div className="p-4 space-y-3">
                  {order.shipments.map((s) => (
                    <div key={s.id} className="flex items-center justify-between text-sm">
                      <div>
                        <p className="font-medium">{s.carrier || 'Carrier'} {s.status ? `— ${s.status}` : ''}</p>
                        {s.tracking_number && <p className="text-muted-foreground">{s.tracking_number}</p>}
                      </div>
                      {s.label_url && (
                        <a href={s.label_url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-primary hover:underline">
                          Label <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: customer + totals + notes */}
          <div className="space-y-6">
            {/* Customer */}
            <div className="bg-card border border-border rounded-lg p-4 space-y-3">
              <p className="font-medium">Customer</p>
              {order.customer_name && <p className="text-sm">{order.customer_name}</p>}
              {order.customer_email ? (
                <a href={`mailto:${order.customer_email}`} className="text-sm text-primary hover:underline block">
                  {order.customer_email}
                </a>
              ) : (
                <p className="text-sm text-muted-foreground">No email on file</p>
              )}
              {order.guest_phone && <p className="text-sm">{order.guest_phone}</p>}
              {order.business_name && (
                <p className="text-sm"><span className="text-muted-foreground">Business:</span> {order.business_name}</p>
              )}
              {order.school_name && (
                <p className="text-sm"><span className="text-muted-foreground">School:</span> {order.school_name}</p>
              )}
              {order.fulfillment_method && (
                <p className="text-sm"><span className="text-muted-foreground">Fulfillment:</span> {order.fulfillment_method.replace(/_/g, ' ')}</p>
              )}
              <AddressBlock title="Shipping Address" address={order.shipping_address} />
              <AddressBlock title="Billing Address" address={order.billing_address} />
            </div>

            {/* Totals */}
            <div className="bg-card border border-border rounded-lg p-4 space-y-2 text-sm" data-testid="order-totals">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{money(order.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Tax</span><span>{money(order.tax)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Shipping</span><span>{money(order.shipping_cost)}</span></div>
              {order.rush_fee > 0 && (
                <div className="flex justify-between"><span className="text-muted-foreground">Rush Fee</span><span>{money(order.rush_fee)}</span></div>
              )}
              {order.discount > 0 && (
                <div className="flex justify-between text-green-600"><span>Discount</span><span>-{money(order.discount)}</span></div>
              )}
              <div className="flex justify-between font-bold text-base pt-2 border-t border-border">
                <span>Total</span><span>{money(order.total)}</span>
              </div>
            </div>

            {/* Notes */}
            {(order.notes || order.admin_notes) && (
              <div className="bg-card border border-border rounded-lg p-4 space-y-3 text-sm">
                {order.notes && (
                  <div>
                    <p className="font-medium mb-1">Order Notes</p>
                    <p className="text-muted-foreground whitespace-pre-wrap break-words">{order.notes}</p>
                  </div>
                )}
                {order.admin_notes && (
                  <div>
                    <p className="font-medium mb-1">Admin Notes</p>
                    <p className="text-muted-foreground whitespace-pre-wrap break-words">{order.admin_notes}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {showShipping && (
        <ShippingLabelModal
          order={order}
          onClose={() => setShowShipping(false)}
          onLabelCreated={() => { setShowShipping(false); fetchOrder(); }}
        />
      )}
    </AdminLayout>
  );
};

export default AdminOrderDetail;
